import React, { useEffect, useRef, useState } from 'react';
import { ListChecks } from 'lucide-react';

// Botón «Acciones (N)» + menú con lo que se puede hacer a la vez con las
// facturas marcadas con la casilla. Copia el desplegable «Campos»
// (FieldPicker): mismas piezas dt-campos-* y mismo cierre (clic fuera o
// Escape). El botón está siempre en su sitio: sin nada marcado queda inactivo,
// y cada acción sin facturas a las que aplicarse queda inactiva en su sitio.
// Va a la izquierda, junto a las casillas de marcar todas, así que el menú se
// abre hacia la derecha (dt-acciones-pop); el de «Campos» se abre hacia la
// izquierda porque su botón va a la derecha.
//
// Props:
//   count    : facturas marcadas a las que se aplican las acciones.
//   scope    : texto corto del periodo al que se aplican ("del T3 de 2026").
//   actions  : [{ key, label, icon: Componente, title, disabled, onClick }]
//   disabled : bloquea el botón entero (p. ej., mientras corre una acción).
const AccionesSeleccion = ({ count = 0, scope = '', actions = [], disabled = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const inactive = disabled || count === 0;

  // Si se desmarca todo (o empieza a correr una acción) con el menú abierto,
  // se cierra: no queda nada a lo que aplicar.
  useEffect(() => { if (inactive) setOpen(false); }, [inactive]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="dt-campos-wrap" ref={ref}>
      <button
        type="button"
        className="btn btn-outline btn-sm dt-campos-btn dt-acciones-btn"
        style={count > 0 ? { borderColor: 'var(--primary)', color: 'var(--primary)' } : undefined}
        onClick={() => { if (!inactive) setOpen((o) => !o); }}
        disabled={inactive}
        aria-haspopup="menu"
        aria-expanded={open}
        title={count === 0
          ? 'Marca facturas con la casilla para actuar sobre todas a la vez'
          : `Qué hacer con las ${count} factura${count === 1 ? '' : 's'} marcada${count === 1 ? '' : 's'}`}
      >
        <ListChecks size={15} /> Acciones{count > 0 ? ` (${count})` : ''}
      </button>
      {open && (
        <div className="dt-campos-pop dt-acciones-pop" role="menu">
          <div className="dt-campos-head">
            <span className="dt-campos-title">{count} marcada{count === 1 ? '' : 's'}</span>
            {scope && <span className="dt-campos-hint">{scope}</span>}
          </div>
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.key}
                type="button"
                role="menuitem"
                className="dt-acciones-item"
                disabled={!!a.disabled}
                title={a.title}
                onClick={() => { setOpen(false); a.onClick(); }}
              >
                {Icon && <Icon size={15} />}
                <span>{a.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccionesSeleccion;
